import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import EventCard from "../components/EventCard";
import { EVENT_TYPES } from "../components/Badges";

const EventsFeed = () => {
  const [events, setEvents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_type: "", vendor_id: "", severity: "", needs_confirm: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "") params[k] = v;
      });
      const r = await api.get("/events", { params });
      setEvents(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  const confirm = async (event) => {
    try {
      await api.post(`/events/${event.id}/confirm`, {});
      toast.success("Event confirmed — vendor memory updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (event) => {
    try {
      await api.post(`/events/${event.id}/reject`);
      toast.success("Event rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmCount = events.filter((e) => e.needs_confirm).length;

  return (
    <div data-testid="events-feed-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Business Events</h1>
          <p className="text-base text-slate-600 dark:text-slate-400">Every brand communication, collapsed into structured events.</p>
        </div>
        <Link
          to="/capture"
          data-testid="events-capture-cta"
          className="rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
        >
          + Capture
        </Link>
      </div>

      {confirmCount > 0 && (
        <Link
          to="/confirm"
          data-testid="events-confirm-banner"
          className="flex items-center gap-3 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
        >
          <Inbox className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <span className="text-base font-semibold text-amber-700 dark:text-amber-300">{confirmCount} event{confirmCount > 1 ? "s" : ""} need your confirm</span>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="events-filter-bar">
        <select
          data-testid="filter-event-type"
          value={filters.event_type}
          onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base text-slate-700 dark:text-slate-300 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        >
          <option value="">All types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          data-testid="filter-vendor"
          value={filters.vendor_id}
          onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base text-slate-700 dark:text-slate-300 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        >
          <option value="">All brands</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <select
          data-testid="filter-severity"
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base text-slate-700 dark:text-slate-300 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        >
          <option value="">All severities</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
        <select
          data-testid="filter-confirm-status"
          value={filters.needs_confirm}
          onChange={(e) => setFilters({ ...filters, needs_confirm: e.target.value })}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base text-slate-700 dark:text-slate-300 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        >
          <option value="">All statuses</option>
          <option value="true">Needs confirm</option>
          <option value="false">Confirmed / auto</option>
        </select>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="events-loading">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div data-testid="events-empty" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center text-base text-slate-600 dark:text-slate-400">
          No events match these filters. Capture a WhatsApp forward to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="events-list">
          {events.map((e) => (
            <EventCard key={e.id} event={e} onConfirm={confirm} onReject={reject} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsFeed;
